import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { API_URL } from '../utils/apiBase';
import { safeSessionSet } from '../utils/safeStorage';

/**
 * Auth state — Context-based so all consumers (Header, AuthGateModal,
 * Dashboard, AIChat, App, …) share a SINGLE user object. Previously this
 * was a plain hook with per-component useState, which meant a successful
 * email/password signup updated only the modal's local copy and Header
 * never re-rendered (Google OAuth happened to work because it triggers
 * a full page reload via /auth/callback).
 *
 * Public API kept identical to the previous useAuth() export, so callers
 * import { useAuth } from '../hooks/useAuth' unchanged.
 *
 * Consumers must be wrapped by <AuthProvider> mounted once at App root.
 */

const AuthContext = createContext(null);

async function fetchMe() {
  try {
    const res = await fetch(`${API_URL}/api/auth/me`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    if (process.env.NODE_ENV === 'development') console.warn('[useAuth] fetchMe failed:', err);
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const u = await fetchMe();
    setUser(u);
    setLoading(false);
    // Phase-4 attribution: persist user_id so analytics can correlate
    // pre-login and post-login events to the same account.
    try {
      if (u && u.user_id) localStorage.setItem('travelai_user_id', u.user_id);
      else localStorage.removeItem('travelai_user_id');
    } catch { /* private mode */ }
    return u;
  }, []);

  // Initial check on mount — runs once for the whole app
  useEffect(() => {
    let active = true;
    fetchMe().then((u) => {
      if (active) {
        setUser(u);
        setLoading(false);
        try {
          if (u && u.user_id) localStorage.setItem('travelai_user_id', u.user_id);
          else localStorage.removeItem('travelai_user_id');
        } catch { /* private mode */ }
      }
    });
    return () => { active = false; };
  }, []);

  const signInWithGoogle = useCallback((returnTo) => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    if (returnTo) {
      safeSessionSet('post_login_return', returnTo);
    }
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('[useAuth] logout failed:', err);
    }
    setUser(null);
  }, []);

  /** Email + password registration. Returns {ok, error?}. */
  const signUpWithEmail = useCallback(async ({ email, password, name, rememberMe = true }) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, remember_me: rememberMe }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail = body?.detail;
        const msg = Array.isArray(detail)
          ? detail.map((d) => d?.msg || JSON.stringify(d)).join(' ')
          : (typeof detail === 'string' ? detail : 'Registration failed');
        return { ok: false, error: msg };
      }
      const data = await res.json();
      setUser(data.user);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || 'Network error' };
    }
  }, []);

  /** Email + password login. Returns {ok, error?}. */
  const signInWithEmail = useCallback(async ({ email, password, rememberMe = false }) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, remember_me: rememberMe }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail = body?.detail;
        const msg = Array.isArray(detail)
          ? detail.map((d) => d?.msg || JSON.stringify(d)).join(' ')
          : (typeof detail === 'string' ? detail : 'Login failed');
        return { ok: false, error: msg };
      }
      const data = await res.json();
      setUser(data.user);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || 'Network error' };
    }
  }, []);

  /** Patch the current user's profile (phone, country, optional name).
   * Updates the shared user state on success so the booking gate clears
   * everywhere the moment the user submits their profile. */
  const updateProfile = useCallback(async ({ phone, country, name }) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, country, name }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail = body?.detail;
        const msg = Array.isArray(detail)
          ? detail.map((d) => d?.msg || JSON.stringify(d)).join(' ')
          : (typeof detail === 'string' ? detail : 'Profile update failed');
        return { ok: false, error: msg };
      }
      const updated = await res.json();
      setUser(updated);
      return { ok: true, user: updated };
    } catch (err) {
      return { ok: false, error: err.message || 'Network error' };
    }
  }, []);

  // Supabase-era compatibility shims — kept so existing callers don't break.
  const signIn = signInWithEmail;
  const signUp = signUpWithEmail;
  const resetPassword = async () => ({ error: { message: 'Password reset coming soon — please contact support.' } });
  const updatePassword = async () => ({ error: { message: 'Password reset coming soon — please contact support.' } });

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: !!user?.is_admin,
    isProfileComplete: !!user?.profile_completed,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    refresh,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    // Defensive: tells the developer they forgot to wrap with AuthProvider.
    throw new Error('useAuth must be used inside <AuthProvider>. Mount it once at App root.');
  }
  return ctx;
}
